/**
 * Utility functions for handling dates in the application
 */
/**
 * Parse a date string or Date object into a Date object
 *
 * @param date - Date string or Date object
 * @returns Date object or null if invalid
 */
export const parseDate = (date: string | Date): Date | null => {
  if (!date) return null
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date
  }
  // Try to parse the date string
  try {
    const parsedDate = new Date(date)
    return isNaN(parsedDate.getTime()) ? null : parsedDate
  } catch (error) {
    console.error('Error parsing date:', error)
    return null
  }
}
/**
 * Check if a date is valid
 *
 * @param date - Date string or Date object
 * @returns boolean indicating if date is valid
 */
export const isValidDate = (date: string | Date): boolean => {
  return parseDate(date) !== null
}
/**
 * Format a date with the specified options
 *
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string => {
  const parsedDate = parseDate(date)
  if (!parsedDate) {
    return 'Invalid date'
  }
  try {
    return new Intl.DateTimeFormat('en-US', options).format(parsedDate)
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Error formatting date'
  }
}
/**
 * Get the difference in days between two dates
 *
 * @param date1 - First date
 * @param date2 - Second date (defaults to current date)
 * @returns Number of days difference or null if invalid dates
 */
export const getDaysDifference = (
  date1: string | Date,
  date2: string | Date = new Date(),
): number | null => {
  const parsedDate1 = parseDate(date1)
  const parsedDate2 = parseDate(date2)
  if (!parsedDate1 || !parsedDate2) {
    return null
  }
  const diffTime = Math.abs(parsedDate2.getTime() - parsedDate1.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}
/**
 * Check if a date is in the past
 *
 * @param date - Date to check
 * @returns boolean indicating if date is in the past
 */
export const isDateInPast = (date: string | Date): boolean => {
  const parsedDate = parseDate(date)
  if (!parsedDate) {
    return false
  }
  return parsedDate < new Date()
}
/**
 * Check if a date is in the future
 *
 * @param date - Date to check
 * @returns boolean indicating if date is in the future
 */
export const isDateInFuture = (date: string | Date): boolean => {
  const parsedDate = parseDate(date)
  if (!parsedDate) {
    return false
  }
  return parsedDate > new Date()
}
/**
 * Add days to a date
 *
 * @param date - Base date
 * @param days - Number of days to add
 * @returns New date with days added or null if invalid date
 */
export const addDays = (date: string | Date, days: number): Date | null => {
  const parsedDate = parseDate(date)
  if (!parsedDate) {
    return null
  }
  const result = new Date(parsedDate)
  result.setDate(result.getDate() + days)
  return result
}
